import { END_POINTS } from "../constants/urls";
import emptySplitApi from "../utils/rtk";

type CommonResponse = {
  status: number;
  data?: any;
  message: string;
};

export const offerApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    createOffer: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.createOffer,
        method: "POST",
        body,
      }),
    }),

    getOffers: builder.query<
      CommonResponse,
      {
        search: string;
        page: number;
        categoryId?: string | null;
        status?: string;
        sortOrder?: string;
        body?: any;
      }
    >({
      query: ({ search, page, categoryId, status, sortOrder, body }) => {
        const params = new URLSearchParams({
          search,
          pageNo: String(page),
        });

        if (categoryId) {
          params.append("categoryId", categoryId);
        }

        if (status) {
          params.append("status", status);
        }

        if (sortOrder) {
          params.append("sortOrder", sortOrder);
        }

        return {
          url: `${END_POINTS.getOffers}?${params.toString()}`,
          method: "POST",
          body,
        };
      },
    }),

    tagsByGroups: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.multipleGroupTags,
        method: "POST",
        body,
      }),
    }),

    getOfferById: builder.query<
      CommonResponse,
      { offerId: string | undefined }
    >({
      query: ({ offerId }) => ({
        url: `${END_POINTS.getOffers}/${offerId}`,
        method: "GET",
      }),
    }),

    getButterflyEffect: builder.query<
      CommonResponse,
      { search: string | undefined }
    >({
      query: ({ search }) => ({
        url: `${END_POINTS.getButterflyEffect}?search=${search}`,
        method: "GET",
      }),
    }),

    setButterflyOrder: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.setButterflyEffectOrder,
        method: "POST",
        body,
      }),
    }),

    setCategoryOrder: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.setCategoryOrder,
        method: "POST",
        body,
      }),
    }),

    getButterflyShowHide: builder.query<
      CommonResponse,
      { id: string | undefined }
    >({
      query: ({ id }) => ({
        url: `${END_POINTS.butterflyShowHide}?offerId=${id}`,
        method: "GET",
      }),
    }),

    updateOffer: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: `${END_POINTS.updateOffer}`,
        method: "PATCH",
        body,
      }),
    }),

    addUserForDining: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.addUserForDining,
        method: "POST",
        body,
      }),
    }),

    addUserForEvent: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: END_POINTS.addUserForEvent,
        method: "POST",
        body,
      }),
    }),

    getOfferBookingHistory: builder.query<
      CommonResponse,
      { offerId: string | undefined; status: string; page: number }
    >({
      query: ({ offerId, status, page }) => ({
        url: `${END_POINTS.offerBookingHistory}?pageNumber=${page}&offerId=${offerId}&bookingHistory=${status}`,
        method: "GET",
      }),
    }),

    changeOfferStatus: builder.mutation<
      CommonResponse,
      { offerId: string | undefined }
    >({
      query: ({ offerId }) => ({
        url: `${END_POINTS.offerStatus}/${offerId}`,
        method: "POST",
      }),
    }),

    getCancelledOffers: builder.query<CommonResponse, {}>({
      query: () => ({
        url: `${END_POINTS.getCancelledOffers}`,
        method: "GET",
      }),
    }),

    getCancelledOfferMembers: builder.query<
      CommonResponse,
      { search: string; offerId: string }
    >({
      query: ({ search, offerId }) => ({
        url: `${END_POINTS.cancelledOfferMembers}?offerId=${offerId}&search=${search}`,
        method: "GET",
      }),
    }),

    addComplimentaryBooking: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: `${END_POINTS.addComplimentaryBooking}`,
        method: "POST",
        body,
      }),
    }),

    inviteUserBooking: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: `${END_POINTS.addInviteUser}`,
        method: "POST",
        body,
      }),
    }),

    getUserDetailByEmail: builder.query<CommonResponse, { email: string }>({
      query: ({ email }) => ({
        url: `${END_POINTS.userDetailByEmail}/${email}`,
        method: "GET",
      }),
    }),

    getBookingMembers: builder.query<
      CommonResponse,
      { offerId: string; search: string }
    >({
      query: ({ offerId, search }) => ({
        url: `${END_POINTS.bookingMembers}?offerId=${offerId}&search=${search}`,
        method: "GET",
      }),
    }),

    cancelEvent: builder.mutation<
      CommonResponse,
      { offerId: string | undefined; CancelType: string }
    >({
      query: ({ offerId, CancelType }) => ({
        url: `${END_POINTS.cancelEvent}?offerId=${offerId}&CancelType=${CancelType}`,
        method: "POST",
      }),
    }),

    eventRefund: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: `${END_POINTS.eventRefund}`,
        method: "POST",
        body,
      }),
    }),

    cloneOffer: builder.query<CommonResponse, { offerId: string | undefined }>({
      query: ({ offerId }) => ({
        url: `${END_POINTS.cloneOffer}?offerId=${offerId}`,
        method: "GET",
      }),
    }),

    stopOfferBooking: builder.mutation<
      CommonResponse,
      { offerId: string | undefined }
    >({
      query: ({ offerId }) => ({
        url: `${END_POINTS.stopOfferBooking}?offerId=${offerId}`,
        method: "POST",
      }),
    }),
    addPaidBooking: builder.mutation<CommonResponse, any>({
      query: (body) => ({
        url: `${END_POINTS.addPaidUser}`,
        method: "POST",
        body,
      }),
    }),

    exportOffers: builder.query<
      CommonResponse,
      {
        search: string;
        categoryId?: string | null;
        status?: string;
        sortOrder?: string;
        body?: any;
        format?: string;
      }
    >({
      query: ({ search, categoryId, status, sortOrder, body, format }) => {
        const params = new URLSearchParams({
          search,
        });

        if (categoryId) {
          params.append("categoryId", categoryId);
        }

        if (status) {
          params.append("status", status);
        }

        if (sortOrder) {
          params.append("sortOrder", sortOrder);
        }

        if (format) {
          params.append("format", format);
        }

        return {
          url: `${END_POINTS.exportOffer}?${params.toString()}`,
          method: "POST",
          body,
        };
      },
    }),

    exportButterflyEffect: builder.mutation<CommonResponse, { format: string }>(
      {
        query: ({ format }) => ({
          url: `${END_POINTS.exportButterflyEffect}?format=${format}`,
          method: "POST",
        }),
      }
    ),

    exportOfferById: builder.mutation<
      CommonResponse,
      { format: string; offerId: string | undefined }
    >({
      query: ({ format, offerId }) => ({
        url: `${END_POINTS.exportOfferById}?format=${format}&offerId=${offerId}`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useCreateOfferMutation,
  useLazyGetOffersQuery,
  useTagsByGroupsMutation,
  useLazyGetOfferByIdQuery,
  useLazyGetButterflyEffectQuery,
  useSetButterflyOrderMutation,
  useLazyGetButterflyShowHideQuery,
  useSetCategoryOrderMutation,
  useUpdateOfferMutation,
  useAddUserForDiningMutation,
  useAddUserForEventMutation,
  useLazyGetOfferBookingHistoryQuery,
  useChangeOfferStatusMutation,
  useLazyGetCancelledOffersQuery,
  useLazyGetCancelledOfferMembersQuery,
  useAddComplimentaryBookingMutation,
  useInviteUserBookingMutation,
  useLazyGetUserDetailByEmailQuery,
  useLazyGetBookingMembersQuery,
  useCancelEventMutation,
  useEventRefundMutation,
  useLazyCloneOfferQuery,
  useStopOfferBookingMutation,
  useAddPaidBookingMutation,
  useLazyExportOffersQuery,
  useExportButterflyEffectMutation,
  useExportOfferByIdMutation,
} = offerApi;
